<script setup lang="ts">
/**
 * AddToCalendarMenu — five-item flyout used anywhere a live event is surfaced:
 * Download .ics, Google Calendar, Outlook (personal), Outlook (work or school),
 * Email Invite. Outlook is split because there is no single deeplink domain
 * that handles both consumer outlook.com and Office 365 work/school accounts.
 *
 * Caller supplies the trigger via the `trigger` slot (passing `toggle` and
 * `isOpen`) so the same component can render next to a date chevron in a list
 * row and as a standalone button on the course detail banner. The parent owns
 * the Email Invite modal; this component only emits `openEmailInvite` when
 * that item is clicked.
 *
 * The menu is teleported to <body> and positioned with `position: fixed` so
 * stacking contexts and `overflow` clipping in ancestor rows can't truncate
 * its bottom items. Position is recomputed on toggle and while the menu is
 * open via scroll/resize listeners.
 *
 * Keyboard (WCAG 2.1 AA, UX-Specifications "Dropdowns"): `useDropdown` moves
 * focus into the menu on open and handles Arrow/Home/End/Escape. Because the
 * menu is teleported, it is NOT next to the trigger in DOM order, so the
 * browser's native Tab order would skip it. Tab / Shift+Tab are therefore moved
 * by hand between the items (wrapping, like the arrow keys) and never leave the
 * menu; Escape closes it and returns focus to the trigger (BPE-803).
 */
import { computed, onMounted, onUnmounted, ref, useId } from 'vue';
import { useDropdown } from '@/composables/useDropdown';
import { liveEventApi } from '@/services/liveEventApi';
import { useToastStore } from '@/stores';
import { downloadBlob } from '@/utils/downloadBlob';

interface Props {
  liveEventId: number;
  eventTitle: string;
  /** ISO-8601 UTC timestamp, e.g. "2026-05-28T16:00:00Z". */
  scheduledAtUtc: string;
  durationMinutes: number;
  /** Absolute URL to the course detail page. Used for LOCATION and deep-links. */
  courseUrl: string;
  courseDescription?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  openEmailInvite: [];
}>();

const toastStore = useToastStore();

// `useDropdown` looks the menu up by id; teleporting to <body> doesn't affect
// getElementById. useId (not liveEventId) keeps ids unique when the same event
// is rendered twice on one page, e.g. dashboard + list.
const menuId = `add-to-calendar-menu-${useId()}`;
const {
  isOpen,
  focusedIndex,
  toggle: toggleDropdown,
  close: closeDropdown,
} = useDropdown(menuId);
let triggerEl: HTMLElement | null = null;

const rootRef = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

// ── Position the teleported menu against the trigger's bounding rect ────────

function updateMenuPosition() {
  const root = rootRef.value;
  if (!root) return;
  const rect = root.getBoundingClientRect();
  // Right-align the menu with the trigger (matches the original .table-dropdown-menu
  // pattern). `position: fixed` keeps the menu locked to viewport coordinates so
  // ancestor stacking and overflow can't clip its bottom edge.
  menuStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
    left: 'auto',
  };
}

function toggle(event: Event) {
  event.stopPropagation();
  triggerEl = event.currentTarget as HTMLElement | null;
  // Position BEFORE opening: useDropdown focuses the first item right after the
  // menu is shown, and focusing a not-yet-positioned fixed menu would scroll
  // the page to wherever the teleported element sits (end of <body>).
  if (!isOpen.value) updateMenuPosition();
  toggleDropdown(event);
}

// Wrapper so `@click="close"` doesn't pass the MouseEvent as closeDropdown's
// `returnFocus` flag.
function close() {
  closeDropdown();
}

function handleTabKey(event: KeyboardEvent) {
  if (!isOpen.value || event.key !== 'Tab') return;

  const items = Array.from(
    document.getElementById(menuId)?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
  );
  const current = items.indexOf(document.activeElement as HTMLElement);
  if (current === -1) {
    // Focus is outside the menu (e.g. opened by mouse, focus on the trigger).
    // useDropdown closes on Tab; refocus the trigger so Tab/Shift+Tab continues
    // from the trigger's place in the page instead of dropping to <body>.
    triggerEl?.focus();
    return;
  }

  // Keep Tab inside the menu. stopImmediatePropagation so useDropdown's own
  // Tab handler (close on Tab) doesn't run.
  event.preventDefault();
  event.stopImmediatePropagation();
  const next = (current + (event.shiftKey ? -1 : 1) + items.length) % items.length;
  // Keep useDropdown's index in sync so ArrowDown/ArrowUp continue from here.
  focusedIndex.value = next;
  items[next]?.focus();
}

function handleViewportChange() {
  if (isOpen.value) updateMenuPosition();
}

onMounted(() => {
  // Capture phase: must run before useDropdown's Tab handler flips isOpen.
  document.addEventListener('keydown', handleTabKey, true);
  // Capture-phase scroll: catch scrolls on any ancestor (rows / panels) too.
  window.addEventListener('scroll', handleViewportChange, true);
  window.addEventListener('resize', handleViewportChange);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleTabKey, true);
  window.removeEventListener('scroll', handleViewportChange, true);
  window.removeEventListener('resize', handleViewportChange);
});

// ── Deep-link URL builders ──────────────────────────────────────────────────

const startUtc = computed(() => new Date(props.scheduledAtUtc));
const endUtc = computed(
  () => new Date(startUtc.value.getTime() + props.durationMinutes * 60_000)
);

/** YYYYMMDDTHHmmssZ — Google Calendar's dates= format. */
function toCompactUtc(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** ISO-8601 without milliseconds — Outlook's startdt/enddt format. */
function toOutlookIso(date: Date): string {
  return date.toISOString().replace(/\.\d{3}/, '');
}

const deepLinkBody = computed(() => {
  const parts: string[] = [];
  if (props.courseDescription?.trim()) {
    parts.push(props.courseDescription.trim());
  }
  parts.push(`Join the course: ${props.courseUrl}`);
  return parts.join('\n\n');
});

const googleUrl = computed(() => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: props.eventTitle,
    dates: `${toCompactUtc(startUtc.value)}/${toCompactUtc(endUtc.value)}`,
    details: deepLinkBody.value,
    location: props.courseUrl,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
});

function buildOutlookParams(): string {
  return new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: props.eventTitle,
    startdt: toOutlookIso(startUtc.value),
    enddt: toOutlookIso(endUtc.value),
    body: deepLinkBody.value,
    location: props.courseUrl,
  }).toString();
}

const outlookPersonalUrl = computed(
  () => `https://outlook.live.com/calendar/0/deeplink/compose?${buildOutlookParams()}`
);
const outlookWorkUrl = computed(
  () => `https://outlook.office.com/calendar/0/deeplink/compose?${buildOutlookParams()}`
);

// ── Actions ─────────────────────────────────────────────────────────────────

async function handleDownloadIcs() {
  close();
  try {
    const { blob, filename } = await liveEventApi.downloadIcs(props.liveEventId);
    downloadBlob(blob, filename);
  } catch (err) {
    console.error('Failed to download .ics:', err);
    toastStore.error('Could not download the calendar file. Please try again.');
  }
}

function handleEmailInvite() {
  close();
  emit('openEmailInvite');
}
</script>

<template>
  <div ref="rootRef" class="add-to-calendar">
    <slot name="trigger" :toggle="toggle" :is-open="isOpen" :menu-id="menuId" />

    <Teleport to="body">
      <div
        v-show="isOpen"
        :id="menuId"
        class="add-to-calendar-menu"
        role="menu"
        aria-label="Add to calendar"
        :style="menuStyle"
      >
        <button
          type="button"
          class="dropdown-menu-item"
          role="menuitem"
          @click="handleDownloadIcs"
        >
          <bb-icon name="download" decorative></bb-icon>
          Download .ics
        </button>

        <a
          :href="googleUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="dropdown-menu-item"
          role="menuitem"
          @click="close"
        >
          <bb-icon name="calendar_month" decorative></bb-icon>
          Google Calendar
        </a>

        <a
          :href="outlookPersonalUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="dropdown-menu-item"
          role="menuitem"
          @click="close"
        >
          <bb-icon name="calendar_month" decorative></bb-icon>
          Outlook (personal)
        </a>

        <a
          :href="outlookWorkUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="dropdown-menu-item"
          role="menuitem"
          @click="close"
        >
          <bb-icon name="calendar_month" decorative></bb-icon>
          Outlook (work or school)
        </a>

        <div class="calendar-menu-divider" role="separator" />

        <button
          type="button"
          class="dropdown-menu-item"
          role="menuitem"
          @click="handleEmailInvite"
        >
          <bb-icon name="forward_to_inbox" decorative></bb-icon>
          Email Invite
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.add-to-calendar {
  position: relative;
  display: inline-block;
}
</style>

<style>
/* Unscoped: the menu is teleported to <body> so it sits outside any scoped
   style boundary. Reuses the existing dropdown look and feel from
   components.css (.dropdown-menu-item) without re-declaring it. */
.add-to-calendar-menu {
  background: var(--color-surface-primary, #fff);
  border: 1px solid var(--color-border-light, #dfe1e6);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  padding: 4px 0;
}

.add-to-calendar-menu .calendar-menu-divider {
  height: 1px;
  background: var(--color-border-light, #dfe1e6);
  margin: 4px 0;
}
</style>
